import { EmailCapture } from '@/app/components/ppra/components/ppra-results-page/EmailCapture';
import PageContainer from '@/components/page-container';
import { AnimatePresence, motion } from 'motion/react';
import { FC, useCallback, useState } from 'react';
import { getAnimationVariants } from './DecisionTree.animation.service';
import useDecisionTreeController from './DecisionTree.controller';
import { CallToActions } from './components/CallToActions';
import { StepBar } from './components/StepBar/StepBar';
import { SurveyItem } from './components/SurveyItem';
import {
  HeroVideoInterstitial,
  HeroVideoInterstitialType,
} from './components/SurveyItem/components/HeroVideoInterstitial/HeroVideoInterstitial';
import { EmailAndNameQuestion, EmailQuestion } from './components/SurveyItem/components/Question/questionTypes';
import { EmailAndName } from './components/SurveyItem/components/Question/questionTypes/EmailAndName';
import { AnswerModel, HandleAnswersActions, PaginationActions } from './hooks';
import { DecisionTreeProps } from './types';

export const DecisionTree: FC<DecisionTreeProps> = ({
  userQuestionnaire,
  callback,
  editQuestion,
  backOnFirstSurveyItem,
  isUnclearQuestionsFlow = false,
  header,
  passToStepBar = false,
  onItemViewed,
}) => {
  const {
    normalizedQuestionnaire,
    currentSurveyItem,
    currentSection,
    paginationDispatch,
    activeStep,
    goToNextSurveyItem,
    showBackButton,
    cta,
    answerState,
    answersDispatch,
    submitIDKAnswer,
    summary,
    result,
    steps,
    whyWeAskModalDisclosure,
  } = useDecisionTreeController({
    userQuestionnaire,
    callback,
    editQuestion,
    backOnFirstSurveyItem,
    isUnclearQuestionsFlow,
    onItemViewed,
  });

  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward'>('forward');
  const saveAnswer = useCallback(
    (payload: AnswerModel, complete?: boolean) => {
      answersDispatch({
        type: HandleAnswersActions.SaveAnswer,
        payload: {
          answer: payload,
          surveyItemId: currentSurveyItem,
          sectionId: currentSection,
          complete,
        },
      });
    },
    [currentSurveyItem, currentSection, answersDispatch],
  );

  const section = normalizedQuestionnaire.sections.find((section) => section.id === currentSection);
  if (!section) return null;
  const surveyItem = section.surveyItems.find((item) => item.id === currentSurveyItem);
  if (!surveyItem) return null;

  const animationVariants = getAnimationVariants(surveyItem);

  const handleBack = () => {
    setAnimationDirection('backward');
    whyWeAskModalDisclosure.onClose();
    paginationDispatch({ type: PaginationActions.PreviousSurveyItem });
    // Scroll to the top when navigating back
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    setAnimationDirection('forward');
    goToNextSurveyItem();
  };

  if ((surveyItem.type as any) === 'heroVideoInterstitial') {
    return (
      <HeroVideoInterstitial
        data={surveyItem as unknown as HeroVideoInterstitialType}
        saveAnswer={saveAnswer}
        goToNextSurveyItem={handleNext}
      />
    );
  }

  if (surveyItem.type === 'email') {
    return (
      <EmailCapture
        onBackClick={handleBack}
        showBackButton={showBackButton}
        question={surveyItem as EmailQuestion}
        defaultValue={answerState.answer ?? undefined}
        saveAnswer={saveAnswer}
        goToNextSurveyItem={handleNext}
        isNextDisabled={!answerState.isAnswered}
      />
    );
  }

  if (surveyItem.type === 'emailAndName') {
    return (
      <EmailAndName
        question={surveyItem as EmailAndNameQuestion}
        defaultValue={answerState.answer?.type === 'emailAndName' ? answerState.answer : undefined}
        saveAnswer={saveAnswer}
        goToNextSurveyItem={handleNext}
        isNextDisabled={!answerState.isAnswered}
      />
    );
  }

  const showCta =
    surveyItem.surveyItemType === 'question' ||
    (surveyItem.surveyItemType === 'interstitial' && surveyItem.type === 'infoImageInterstitial');

  return (
    <PageContainer>
      <PageContainer.Header>
        {header &&
          header({
            onBackClick: handleBack,
            showBackButton,
          })}

        {!editQuestion && !isUnclearQuestionsFlow && surveyItem.type !== 'sectionDivider' && (
          <StepBar steps={steps} activeStep={activeStep} userQuestionnaire={userQuestionnaire} />
        )}
      </PageContainer.Header>

      <PageContainer.Content bottomPadding={showCta ? { base: '50px', md: '80px' } : undefined}>
        <AnimatePresence mode="wait" custom={animationDirection}>
          <motion.div
            key={currentSurveyItem}
            custom={animationDirection}
            variants={animationVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{
              width: '100%',
              // TODO: Remove this once we handle height in other survey items
              ...(surveyItem.type === 'infoImageInterstitial' && { height: '100%' }),
            }}
          >
            <SurveyItem
              surveyItem={surveyItem}
              paginationDispatch={paginationDispatch}
              defaultValue={answerState.answer ?? undefined}
              summary={summary ?? undefined}
              result={result ?? []}
              submitIDKAnswer={() => submitIDKAnswer(currentSurveyItem)}
              saveAnswer={saveAnswer}
              goToNextSurveyItem={handleNext}
              whyWeAskModalDisclosure={whyWeAskModalDisclosure}
            />
          </motion.div>
        </AnimatePresence>
      </PageContainer.Content>

      <PageContainer.Footer>
        {showCta && (
          <CallToActions
            isNextDisabled={
              !answerState.isAnswered && currentSurveyItem !== 'summary' && currentSurveyItem !== 'result'
            }
            goToNextSurveyItem={handleNext}
            cta={cta}
          />
        )}
      </PageContainer.Footer>
    </PageContainer>
  );
};
